import PropTypes from 'prop-types';
import {useState} from 'react';
import {createUseStyles} from 'react-jss';
import {connect} from 'react-redux';
import deleteIcon from '../../../../svg/icons/delete.svg';
import editIcon from '../../../../svg/icons/edit.svg';
import MeatballMenu from '../../../UI/MeatballMenu/MeatballMenu';
import OnFieldPopover from '../../../UI/OnFieldPopover/OnFieldPopover';
import Translation from '../../lang/Translation';
import {getSettings} from '../../selectors/selectors';
import {TooltipAndOverlayIDs} from '../detailed-tooltip/TooltipLibrary';
import AddressAsText from './AddressAsText/AddressAsText';
import AddressFields from './AddressFields';
import AddressHelpMessage from './AddressHelpMessage';
import CountrySelect from './CountrySelect';
import addressBookIcon from './DefaultAddress/DefaultAddressLogos/address_book_icon.svg';
import cardIcon from './DefaultAddress/DefaultAddressLogos/card_icon.svg';
import truckIcon from './DefaultAddress/DefaultAddressLogos/truck_icon.svg';
import DefaultAddressSection from './DefaultAddress/DefaultAddressSection';

const useStyles = createUseStyles({
  fieldWrapper: {
    width: props => props.inEditDeleteExperiment && '100%',
  },
  addressContainerWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: props => (props.showDefaultAddressSection && (props.defaultBillingAddress || props.defaultDeliveryAddress)) && 36,
  },
  editDeleteAddressMenu: {
    position: 'absolute',
    right: 0,
    bottom: props => (props.showDefaultAddressSection && (props.defaultBillingAddress || props.defaultDeliveryAddress) ? 24 : 0),
  },
  deliveryCountry: {
    marginBottom: props => props.addressType !== 'delivery' && 24,
  },
  defaultAddressSection: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
  },
});

export function AddressContainer(props) {
  const classes = useStyles({...props});
  const [showCountryChangeTooltip, setShowCountryChangeTooltip] = useState(false);
  
  let defaultIcon = cardIcon;
  if (props.defaultDeliveryAddress && !props.defaultBillingAddress) {
    defaultIcon = truckIcon;
  } else if (props.defaultDeliveryAddress && props.defaultBillingAddress) {
    defaultIcon = addressBookIcon;
  }
  
  let defaultAddressType = (!props.defaultDeliveryAddress && props.defaultBillingAddress) ? 'defaultBillingAddress' : '';
  defaultAddressType = (props.defaultDeliveryAddress && !props.defaultBillingAddress) ? 'defaultDeliveryAddress' : defaultAddressType;
  defaultAddressType = (props.defaultDeliveryAddress && props.defaultBillingAddress) ? 'defaultDeliveryAndBillingAddress' : defaultAddressType;
  
  const defaultAddressTranslation = `checkout.delivery.default.${defaultAddressType}`;
  
  const getFilterCondition = () => {
    if (props.addressId) {
      if (props.isIncludeExtraFields) {
        return definedField => definedField.isExtra
          && ((!definedField.filterCondition) || definedField.filterCondition(props));
      }
      return () => false;
    }
    return definedField => !definedField.filterCondition || definedField.filterCondition(props);
  };
  
  const resetAddress = (newValues) => {
    props.resetAddress(newValues, props.addressId);
  };
  
  const hideCountryChangeTooltip = () => {
    setShowCountryChangeTooltip(false);
  };
  
  const handleCountryChange = (e) => {
    setShowCountryChangeTooltip(true);
    // Hide the popover after a set time.
    setTimeout(hideCountryChangeTooltip, 5000);
    return props.onCountryChange(e);
  };
  
  const filterCondition = getFilterCondition();
  
  const handleClick = () => {
    if (props.selectedAddressId !== props.addressId) {
      props.onClick({addressId: props.addressId, selectedAddressCategory: props.selectedAddressCategory});
    }
  };
  
  return (
    <div
      className={`address-container fields ${props.addressType}-address-fields ${props.className}
      ${classes.fieldWrapper}`}
    >
      {props.addressId && (
        <div>
          <div className={classes.addressContainerWrapper}>
            <AddressAsText
              inEditDeleteExperiment={props.inEditDeleteExperiment}
              {...props.fieldValues}
              addressId={props.addressId}
            />
            {props.inEditDeleteExperiment && (
              <div className={classes.editDeleteAddressMenu}>
                <MeatballMenu
                  id={`addressOptions-${props.addressId}`}
                  overlayId={TooltipAndOverlayIDs.EDIT_DELETE_ADDRESS}
                  overlayDetails={{addressId: props.addressId}}
                  onClick={handleClick}
                  address={props.fieldValues}
                  listItems={[
                    {
                      icon: editIcon,
                      name: 'EDIT',
                      translationKey: 'checkout.delivery.editDeleteAddress.overlay.edit',
                      overlayId: TooltipAndOverlayIDs.EDIT_ADDRESS,
                    },
                    {
                      icon: deleteIcon,
                      name: 'DELETE',
                      translationKey: 'checkout.delivery.editDeleteAddress.overlay.delete',
                      overlayId: TooltipAndOverlayIDs.DELETE_ADDRESS,
                    },
                  ]}
                />
              </div>
            )}
          </div>
          {(props.showDefaultAddressSection && (props.defaultDeliveryAddress || props.defaultBillingAddress)) && (
            <div className={classes.defaultAddressSection}>
              <DefaultAddressSection
                id="defaultAddressSection"
                defaultAddressType={{
                  icon: defaultIcon,
                  name: defaultAddressType,
                  translationKey: defaultAddressTranslation,
                }}
              />
            </div>
          )}
        </div>
      )}
      
      {(!props.addressId && !props.hideCountryRegionDropdown) && (
        <div className={classes.deliveryCountry}>
          <CountrySelect
            disabled={props.disabled}
            addressType={props.addressType}
            countries={props.countries}
            fieldContainer={props.fieldContainer}
            onChange={handleCountryChange}
            fieldNamePrefix={props.fieldNamePrefix}
            value={props.fieldValues.country}
            restrictDeliveryCountry={props.restrictDeliveryCountry}
          />
          
          {props.addressType === 'delivery' && (
            <OnFieldPopover
              show={showCountryChangeTooltip}
            >
              <Translation>popover.deliveryOptionsChange.content</Translation>
            </OnFieldPopover>
          )}
        
        </div>
      )}
      
      {props.fieldValues.country && props.definedFields.filter(filterCondition).length > 0 && (
        <>
          {props.addressType === 'delivery' && <AddressHelpMessage country={props.fieldValues.country} />}
          <AddressFields
            disabled={props.disabled}
            definedFields={props.definedFields.filter(filterCondition)}
            addressType={props.addressType}
            fieldContainer={props.fieldContainer}
            fieldContainerPhone={props.fieldContainerPhone}
            fieldNamePrefix={props.fieldNamePrefix}
            fieldValues={props.fieldValues}
            resetAddress={resetAddress}
            updateDeliveryOptions={props.updateDeliveryOptions}
            onTaxCountyChange={props.onTaxCountyChange}
            useNewDispatcherForBillingAddress={props.useNewDispatcherForBillingAddress}
          />
        </>
      )}
    </div>
  );
}

const DefaultAddressTypePropType = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
};

AddressContainer.propTypes = {
  addressType: PropTypes.oneOf(['delivery', 'billing']).isRequired,
  countries: PropTypes.arrayOf(PropTypes.string).isRequired,
  definedFields: PropTypes.arrayOf(PropTypes.object).isRequired,
  isIncludeExtraFields: PropTypes.bool.isRequired,
  inEditDeleteExperiment: PropTypes.bool,
  fieldContainer: PropTypes.shape({
    deregisterField: PropTypes.func.isRequired,
    registerField: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    validate: PropTypes.func.isRequired,
  }).isRequired,
  fieldContainerPhone: PropTypes.object,
  fieldValues: PropTypes.object.isRequired,
  onCountryChange: PropTypes.func.isRequired,
  hideCountryRegionDropdown: PropTypes.bool.isRequired,
  
  // optional
  addressId: PropTypes.string,
  selectedAddressId: PropTypes.string,
  selectedAddressCategory: PropTypes.string,
  className: PropTypes.string,
  fieldNamePrefix: PropTypes.string,
  resetAddress: PropTypes.func,
  disabled: PropTypes.bool,
  updateDeliveryOptions: PropTypes.func,
  onTaxCountyChange: PropTypes.func,
  restrictDeliveryCountry: PropTypes.func,
  onClick: PropTypes.func,
  useNewDispatcherForBillingAddress: PropTypes.bool,
  defaultBillingAddress: PropTypes.bool,
  defaultDeliveryAddress: PropTypes.bool,
  defaultAddressType: PropTypes.shape(DefaultAddressTypePropType),
  showDefaultAddressSection: PropTypes.bool.isRequired,
};

AddressContainer.defaultProps = {
  fieldNamePrefix: '',
  className: '',
};

const mapStateToProps = state => ({
  showDefaultAddressSection: getSettings(state)?.show_default_address_section_in_address,
});

export default connect(mapStateToProps)(AddressContainer);
